import { AuctionsNearMeRouteParamsOld } from './types/AuctionsNearMe.types';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { createGeoAreaByIdSelector } from '@/redux/modules/geoArea/geoArea.selectors';
import { getPageDescriptionFromGeoArea } from './helpers/getPageDescription';
import { getPageTitle } from './helpers/getPageTitle';
import { LeaderBoardAdTypes } from '@/redux/modules/catalog/ad/catalogAd.types';
import { ReactElement, useEffect, useState } from 'react';
import { recordAnalyticsForNearMePageView } from '@/redux/modules/analytics';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useLocation, useParams } from 'react-router-dom';
import { waitToDispatch } from '@/utils/waitToDispatch/waitToDispatch';
import AuctionsNearMeMapView from './components/MapView/AuctionsNearMeMapView';
import AuctionsNearMePageMeta from './meta/AuctionsNearMePageMeta';
import CategoryAd from '@/components/Ads/CategoryAd';
import PageTitle from '@/components/PageTitle/PageTitle';
import styled from 'styled-components';

const AuctionsNearMePage = (): ReactElement => {
    const params = useParams<AuctionsNearMeRouteParamsOld>();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const [geocoderFailed, setGeocoderFailed] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [hasFilteredResults, setHasFilteredResults] = useState(true);

    const geoArea = useAppSelector(createGeoAreaByIdSelector(Number(params.geoAreaId)));

    useEffect(() => {
        waitToDispatch(recordAnalyticsForNearMePageView, dispatch, location);
    }, [dispatch, location]);

    const adId = 'auctions-near-me';
    const adPageName = `leaderboard-${adId}`;

    return (
        <>
            <CategoryAd
                adType={LeaderBoardAdTypes[adId]}
                pageName={adPageName}
            />
            <StyledContent>
                <AuctionsNearMePageMeta
                    geoArea={geoArea}
                    locationName={geoArea?.geoAreaName}
                />
                <StyledPageInformation>
                    <StyledTitleRow>
                        <PageTitle
                            className="mt-lg"
                            title={getPageTitle({ geoArea, geocoderFailed, hasFilteredResults, searchTerm })}
                        />
                    </StyledTitleRow>
                    <StyledDescription>
                        <BodySecondary color="secondary">{getPageDescriptionFromGeoArea(geoArea)}</BodySecondary>
                    </StyledDescription>
                </StyledPageInformation>
                <AuctionsNearMeMapView
                    geoArea={geoArea}
                    geocoderFailed={geocoderFailed}
                    setGeocoderFailed={setGeocoderFailed}
                    setHasFilteredResults={setHasFilteredResults}
                    setPageTitleSearchTerm={setSearchTerm}
                />
            </StyledContent>
        </>
    );
};

export default AuctionsNearMePage;

const StyledContent = styled.section`
    display: flex;
    position: relative;
    flex-direction: column;
    min-height: 400px;
    padding: 0 20px 50px;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    margin: 0 auto;
    @media (max-width: 1200px) {
        margin-left: 0;
        min-width: 55%;
        max-width: 100%;
        min-height: 300px;
    }
`;

const StyledPageInformation = styled.section`
    margin-bottom: 32px;
`;

const StyledTitleRow = styled.div`
    margin-bottom: 8px;
    width: 100%;
    display: flex;
    justify-content: space-between;
`;

const StyledDescription = styled.div`
    max-width: 590px;
`;
